import React from 'react';
import PropTypes from 'prop-types';
import { colors as COLORS } from '@nerdwallet/base-styles/variables';
import { Box, Text, Title, Accent } from '@nerdwallet/currency';
import classes from '@nerdwallet/base-styles/classes';
import classNames from 'classnames';

// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import identity from 'lodash/identity';
import pipe from 'lodash/fp/pipe';
import replace from 'lodash/fp/replace';

import {
  stringInputSpec,
  stringTextAreaSpec,
  stringSelectSpec,
  boolCheckboxSpec,
} from '../../lib/editing-configs';

import getValidColor from '../../lib/get-valid-color';
import parseMarkdownLinks from '../../lib/parse-md-links';
import './text-box.less';
import parseStyleFormatting from './lib/parse-text-box-formatting';

const copyOptions = [
  'copy-h1',
  'copy-h2',
  'copy-h3',
  'copy-h4',
  'copy-h5',
  'copy-p1',
  'copy-p2',
];

const copyBuilderOptions = copyOptions.map((value) => ({
  label: value.replace('copy-', ''),
  value,
}));

const alignOptions = ['left', 'center', 'right'];

const alignBuilderOptions = alignOptions.map((value) => ({
  label: value.replace(/^\w/, (ch) => ch.toUpperCase()),
  value,
}));

const interpolator = (data) =>
  replace(/#{(\w+[.\w+]+)\}/g, (fullMatch, property) =>
    get(data, property, '')
  );

// transitional helper
// if dangerouslyAllowInnerHTML is true, we should inline the text elements (span instead of p) in case the
// HTML includes a p tag
const toNDS =
  (copyStyle, dangerouslyAllowHTML, fontChronicle, textAlign) => (text) =>
    ({
      // Heading
      'copy-h1': (
        <Title
          className={classNames([
            'text-box-title',
            {
              gotham: !fontChronicle,
            },
          ])}
          component="h4"
          align={textAlign}
        >
          {text}
        </Title>
      ),
      'copy-h2': (
        <Title
          className={classNames([
            'text-box-title',
            {
              gotham: !fontChronicle,
            },
          ])}
          component="h4"
          size="small"
          align={textAlign}
        >
          {text}
        </Title>
      ),
      'copy-h3': (
        <Box
          className={classNames([
            'text-box-title',
            {
              gotham: !fontChronicle,
            },
          ])}
        >
          <Text component="h4" size="large" align={textAlign}>
            {text}
          </Text>
        </Box>
      ),
      'copy-h4': (
        <Box
          className={classNames([
            'text-box-title',
            {
              gotham: !fontChronicle,
            },
          ])}
        >
          <Text component="h4" align={textAlign}>
            {text}
          </Text>
        </Box>
      ),
      'copy-h5': (
        <Box
          className={classNames([
            'text-box-title',
            classes('uppercase'),
            {
              gotham: !fontChronicle,
            },
          ])}
          align={textAlign}
        >
          <Accent size="small">{text}</Accent>
        </Box>
      ),
      // Text
      'copy-p1': <Text>{text}</Text>,
      'copy-p2': <Text size="small">{text}</Text>,
    }[copyStyle]);

const TextBox = ({
  text,
  copyStyle,
  color,
  isHexColor,
  className,
  textAlign,
  parseLinks,
  parseFormatting,
  interpolateFrom,
  dangerouslyAllowHTML,
  fontChronicle,
}) => {
  let colorValue = color;
  if (!isHexColor) {
    colorValue = getValidColor(color) || COLORS['neutral-darkest'];
  }
  const textProps = {
    className,
    style: {
      color: colorValue,
      textAlign,
    },
  };
  const linkParser = (content) =>
    parseLinks ? parseMarkdownLinks(content, dangerouslyAllowHTML) : content;
  const isString = typeof text === 'string';
  const processedText = pipe(
    isString ? interpolator(interpolateFrom) : identity,
    linkParser,
    parseFormatting ? parseStyleFormatting : identity
  )(text);

  const typography = toNDS(
    copyStyle,
    dangerouslyAllowHTML,
    fontChronicle,
    textAlign
  );

  return typography(
    dangerouslyAllowHTML ? (
      <span
        {...textProps}
        // eslint-disable-next-line react/no-danger -- directive added automatically by Shepherd migration
        dangerouslySetInnerHTML={{ __html: processedText }}
      />
    ) : (
      <span {...textProps}>{processedText}</span>
    )
  );
};

TextBox.displayName = 'TextBox';

TextBox.propTypes = {
  text: PropTypes.node.isRequired,
  copyStyle: PropTypes.oneOf(copyOptions).isRequired,
  textAlign: PropTypes.oneOf(alignOptions),
  color: PropTypes.string,
  className: PropTypes.string,
  parseLinks: PropTypes.bool,
  parseFormatting: PropTypes.bool,
  interpolateFrom: PropTypes.object,
  dangerouslyAllowHTML: PropTypes.bool,
  fontChronicle: PropTypes.bool,
};

TextBox.defaultProps = {
  text: '',
  textAlign: 'center',
  copyStyle: 'copy-h1',
  parseLinks: false,
  interpolateFrom: {},
  dangerouslyAllowHTML: false,
  fontChronicle: false,
};

TextBox.getEditingConfig = () => [
  stringTextAreaSpec('text', 'Text'),
  stringSelectSpec('copyStyle', 'Copy Style', copyBuilderOptions),
  stringSelectSpec('textAlign', 'Align', alignBuilderOptions),
  stringInputSpec('className', 'CSS classes'),
  boolCheckboxSpec('parseLinks', 'Parse markdown links?'),
];

export default TextBox;
